import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { resetChat, selectSessionId } from '../store/chatSlice';
import { useEndSessionMutation } from '../api/chatApi';
import { logger } from '@core/utils';

/**
 * Custom hook for starting a new chat conversation
 * Clears history, ends current session, and navigates to home
 *
 * @returns {Function} startNewChat - Function to start a new chat
 *
 * @example
 * const startNewChat = useStartNewChat();
 * <button onClick={startNewChat}>New Chat</button>
 */
export function useStartNewChat() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector(selectSessionId);
  const [endSession] = useEndSessionMutation();

  const startNewChat = async () => {
    // Save current sessionId before clearing (need it to call endSession API)
    const currentSessionId = sessionId;

    // Clear Redux state FIRST (both history and sessionId)
    // This prevents race condition where old sessionId might be used
    dispatch(resetChat());

    // End backend session if one exists
    if (currentSessionId) {
      try {
        await endSession({ sessionId: currentSessionId }).unwrap();
        logger.info('Session ended successfully from useStartNewChat');
      } catch (error) {
        logger.error('Failed to end session in useStartNewChat:', error);
        // Don't throw - navigate anyway since Redux is already cleared
      }
    }

    // Navigate to home (new chat page)
    navigate('/');
  };

  return startNewChat;
}
